import { type Asset } from "@overmap-ai/common"
import { selectAssetById } from "@overmap-ai/core"
import * as turf from "@turf/turf"
import type { Feature } from "geojson"
import type { CircleLayerSpecification, LineLayerSpecification } from "mapbox-gl"
import { memo, useDeferredValue, useMemo } from "react"

import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectAssetsOfProjectAndAssetType } from "../../store/selectors"
import { Layer, Source } from "../components"
import { POINT_GEOMETRY_TYPE_FILTER, POLYGON_GEOMETRY_TYPE_FILTER, SELECTED_FEATURE_COLOR } from "../constants"
import { MapSlotLayerId } from "../enums"
import { flattenGeometry } from "../utils"
import { RENDER_ICONS_THRESHOLD } from "./constants"
import { getCircleLayerSizes } from "./utils"

enum SelectedAssetLayerId {
	CIRCLE = "selected-asset-circle-layer",
	FILL = "selected-asset-fill-layer",
}

interface SelectedAssetLayerProps {
	assetId: Asset["uuid"] | null
}

export const SelectedAssetLayer = memo<SelectedAssetLayerProps>((props) => {
	const { assetId } = props

	const asset = useAppSelector(selectAssetById(assetId ?? ""))
	const assetCount = useShallowEqaulSelector(
		selectAssetsOfProjectAndAssetType(asset?.project ?? "", asset?.asset_type ?? ""),
	).length
	const renderIcons = assetCount <= RENDER_ICONS_THRESHOLD

	const selectedGeoJson = useMemo(() => {
		if (!asset?.uuid) return turf.featureCollection([])

		const features: Feature[] = []

		for (const geometry of flattenGeometry(asset.geometry)) {
			switch (geometry.type) {
				case "Point":
					features.push(turf.feature(geometry))
					break
				case "LineString":
					features.push(turf.buffer(geometry, 1, { units: "meters" })!)
					break
				case "Polygon":
					features.push(turf.buffer(geometry, 0)!)
					break
			}
		}

		return turf.featureCollection(features)
	}, [asset?.geometry, asset?.uuid])

	const defferredSelectedGeoJson = useDeferredValue(selectedGeoJson)

	const { selectedRadius } = useMemo(() => getCircleLayerSizes(renderIcons), [renderIcons])

	const selectedCircleLayerPaint = useMemo(() => {
		return {
			"circle-radius": selectedRadius,
			"circle-stroke-width": 4,
			"circle-stroke-color": SELECTED_FEATURE_COLOR,
			"circle-opacity": 0,
		} satisfies CircleLayerSpecification["paint"]
	}, [selectedRadius])

	const selectedFillLayerPaint = useMemo(() => {
		return {
			"line-color": SELECTED_FEATURE_COLOR,
			"line-width": 4,
			"line-offset": -4,
			"line-opacity": 1,
		} satisfies LineLayerSpecification["paint"]
	}, [])

	return (
		<Source type="geojson" data={defferredSelectedGeoJson}>
			<Layer
				id={SelectedAssetLayerId.CIRCLE}
				type="circle"
				paint={selectedCircleLayerPaint}
				beforeId={MapSlotLayerId.SELECTION}
				filter={POINT_GEOMETRY_TYPE_FILTER}
			/>
			<Layer
				id={SelectedAssetLayerId.FILL}
				type="line"
				paint={selectedFillLayerPaint}
				beforeId={MapSlotLayerId.SELECTION}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
			/>
		</Source>
	)
})
SelectedAssetLayer.displayName = "SelectedAssetLayer"
